<template>
  <b-modal v-model="model" size="2xl">
    <template #header>
      <div class="px-10 pt-10 pb-2">
        <div class="flex items-center gap-3 mb-2">
          <icon name="mdi:linkedin" class="h-8 w-8 text-[#0A66C2]" />
          <h1>{{ $t('linkedin_import.title') }}</h1>
        </div>
        <p class="text-gray-400">{{ $t('linkedin_import.description') }}</p>
      </div>
    </template>

    <template #default>
      <div class="px-10 py-5">
        <!-- Step 1: Enter LinkedIn URL or Upload PDF -->
        <div v-if="currentStep === 'input'" class="space-y-6">
          <!-- Tab switcher (hidden: LinkedIn scraper temporarily disabled, PDF-only flow) -->
          <div v-if="false" class="flex gap-2 bg-gray-900/50 rounded-lg p-1">
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
              :class="importMode === 'pdf' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'"
              @click="importMode = 'pdf'"
            >
              <icon name="solar:document-text-outline" class="h-4 w-4" />
              {{ $t('linkedin_import.tab_pdf') }}
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors"
              :class="importMode === 'url' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'"
              @click="importMode = 'url'"
            >
              <icon name="mdi:link-variant" class="h-4 w-4" />
              {{ $t('linkedin_import.tab_url') }}
            </button>
          </div>

          <!-- PDF Upload tab -->
          <div v-if="importMode === 'pdf'" class="space-y-4">
            <input
              ref="fileInputRef"
              type="file"
              accept="application/pdf"
              class="hidden"
              @change="handleFileSelect"
            />
            <div
              class="bg-gray-900/50 rounded-xl p-8 border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center min-h-[160px]"
              :class="isDragging ? 'border-primary bg-primary/10' : selectedFile ? 'border-green-500/50 bg-green-900/10' : 'border-gray-700 hover:border-gray-500'"
              @click="triggerFileInput"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleFileDrop"
            >
              <template v-if="selectedFile">
                <icon name="solar:document-text-bold" class="h-10 w-10 text-green-400 mb-3" />
                <p class="text-white font-medium">{{ selectedFile.name }}</p>
                <p class="text-gray-400 text-xs mt-1">{{ $t('linkedin_import.pdf_click_to_change') }}</p>
              </template>
              <template v-else>
                <icon name="solar:upload-minimalistic-outline" class="h-10 w-10 text-gray-400 mb-3" />
                <p class="text-white font-medium">{{ $t('linkedin_import.pdf_drop_zone') }}</p>
                <p class="text-gray-400 text-sm mt-1">{{ $t('linkedin_import.pdf_drop_zone_hint') }}</p>
              </template>
            </div>
            <p class="text-gray-400 text-xs">{{ $t('linkedin_import.pdf_hint') }}</p>
          </div>

          <!-- URL tab -->
          <div v-if="importMode === 'url'" class="space-y-4">
            <!-- Scrape failed: show fallback PDF upload -->
            <div v-if="scrapeHasFailed">
              <div class="bg-orange-900/20 border border-orange-500/50 rounded-xl p-6 mb-4">
                <div class="flex items-start gap-3">
                  <icon name="solar:danger-triangle-outline" class="h-6 w-6 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 class="text-orange-400 font-semibold mb-1">{{ $t('linkedin_import.scrape_failed_title') }}</h3>
                    <p class="text-orange-300/80 text-sm">{{ $t('linkedin_import.scrape_failed_description') }}</p>
                  </div>
                </div>
              </div>
              <!-- Reuse the same PDF upload zone -->
              <input
                ref="fileInputRef"
                type="file"
                accept="application/pdf"
                class="hidden"
                @change="handleFileSelect"
              />
              <div
                class="bg-gray-900/50 rounded-xl p-8 border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center min-h-[160px]"
                :class="isDragging ? 'border-primary bg-primary/10' : selectedFile ? 'border-green-500/50 bg-green-900/10' : 'border-gray-700 hover:border-gray-500'"
                @click="triggerFileInput"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleFileDrop"
              >
                <template v-if="selectedFile">
                  <icon name="solar:document-text-bold" class="h-10 w-10 text-green-400 mb-3" />
                  <p class="text-white font-medium">{{ selectedFile.name }}</p>
                  <p class="text-gray-400 text-xs mt-1">{{ $t('linkedin_import.pdf_click_to_change') }}</p>
                </template>
                <template v-else>
                  <icon name="solar:upload-minimalistic-outline" class="h-10 w-10 text-gray-400 mb-3" />
                  <p class="text-white font-medium">{{ $t('linkedin_import.pdf_drop_zone') }}</p>
                  <p class="text-gray-400 text-sm mt-1">{{ $t('linkedin_import.pdf_drop_zone_hint') }}</p>
                </template>
              </div>
              <p class="text-gray-400 text-xs mt-2">{{ $t('linkedin_import.pdf_hint') }}</p>
            </div>

            <!-- Normal URL mode (no failure yet) -->
            <template v-else>
              <!-- Loading status check -->
              <div v-if="isCheckingStatus" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                <span class="text-gray-400">{{ $t('linkedin_import.checking_status') }}</span>
              </div>

              <!-- Scraper unavailable warning -->
              <div v-else-if="!scraperAvailable" class="bg-yellow-900/20 border border-yellow-500/50 rounded-xl p-6">
                <div class="flex items-start gap-3">
                  <icon name="solar:danger-triangle-outline" class="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 class="text-yellow-500 font-semibold mb-1">{{ $t('linkedin_import.unavailable_title') }}</h3>
                    <p class="text-yellow-400/80 text-sm">{{ scraperUnavailableReason }}</p>
                    <p class="text-gray-400 text-xs mt-3">{{ $t('linkedin_import.unavailable_hint') }}</p>
                  </div>
                </div>
              </div>

              <!-- URL input (only shown when scraper is available) -->
              <div v-else class="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <b-text-field
                  v-model="linkedinUrl"
                  :label="$t('linkedin_import.url_label')"
                  :placeholder="$t('linkedin_import.url_placeholder')"
                  :disabled="isScraping"
                />
                <p class="text-gray-400 text-xs mt-2">{{ $t('linkedin_import.url_hint') }}</p>
              </div>
            </template>
          </div>

          <!-- Existing experiences reminder -->
          <div v-if="existingExperiences.length > 0" class="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <h3 class="text-white text-sm font-semibold mb-3 flex items-center gap-2">
              <icon name="solar:info-circle-outline" class="h-4 w-4 text-primary" />
              {{ $t('linkedin_import.existing_experiences_title') }}
            </h3>
            <div class="space-y-2 max-h-40 overflow-y-auto">
              <div
                v-for="exp in existingExperiences"
                :key="exp.id"
                class="flex items-center gap-2 text-sm text-gray-300"
              >
                <icon name="solar:check-circle-bold" class="h-4 w-4 text-green-400 flex-shrink-0" />
                <span class="truncate">{{ exp.title }} - {{ exp.company?.public_name || exp.company?.business_name || 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Scraping/Parsing in progress -->
        <div v-if="currentStep === 'scraping'" class="flex flex-col items-center justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p class="text-white text-lg mb-2">{{ importMode === 'pdf' ? $t('linkedin_import.parsing_title') : $t('linkedin_import.scraping_title') }}</p>
          <p class="text-gray-400 text-sm">{{ importMode === 'pdf' ? $t('linkedin_import.parsing_description') : $t('linkedin_import.scraping_description') }}</p>
        </div>

        <!-- Step 3: Select experiences -->
        <div v-if="currentStep === 'select'" class="space-y-4">
          <div v-if="scrapeError" class="bg-red-900/20 border border-red-500 rounded-xl p-4 mb-4">
            <p class="text-red-400 text-sm">{{ scrapeError }}</p>
          </div>

          <div v-if="scrapedExperiences.length > 0">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-white font-semibold">{{ $t('linkedin_import.select_title') }}</h3>
              <button
                @click="toggleSelectAll"
                class="text-primary text-sm hover:underline"
              >
                {{ allSelected ? $t('linkedin_import.deselect_all') : $t('linkedin_import.select_all') }}
              </button>
            </div>

            <p class="text-gray-400 text-sm mb-4">
              {{ $t('linkedin_import.selected_count', { count: selectedCount, total: scrapedExperiences.length }) }}
            </p>

            <div class="space-y-3 max-h-80 overflow-y-auto pr-2">
              <div
                v-for="(experience, index) in scrapedExperiences"
                :key="index"
                class="bg-gray-900/50 rounded-xl p-4 border border-gray-700 cursor-pointer transition-colors"
                :class="{
                  'border-primary bg-primary/10': selectedExperiences[index],
                  'opacity-50': isAlreadyImported(experience),
                  'border-l-4 border-l-orange-400': experience.confidence !== undefined && experience.confidence < 0.70 && !isAlreadyImported(experience),
                }"
                @click="!isAlreadyImported(experience) && toggleExperience(index)"
              >
                <div class="flex items-start gap-3">
                  <!-- Checkbox -->
                  <div class="flex-shrink-0 mt-1">
                    <div
                      class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                      :class="selectedExperiences[index] ? 'bg-primary border-primary' : 'border-gray-500'"
                    >
                      <icon v-if="selectedExperiences[index]" name="solar:check-read-linear" class="h-3 w-3 text-white" />
                    </div>
                  </div>
                  <div class="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <icon name="solar:buildings-2-outline" class="h-5 w-5 text-gray-400" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-white font-medium truncate">{{ experience.title || 'Poste non specifie' }}</p>
                    <p class="text-gray-400 text-sm truncate">{{ experience.company || 'Entreprise non specifiee' }}</p>
                    <p v-if="experience.start_date" class="text-gray-500 text-xs mt-1">{{ experience.start_date }}{{ experience.end_date ? ` - ${experience.end_date}` : ` - ${$t('linkedin_import.present')}` }}</p>
                    <button
                      v-if="scrapedIsSplittable(experience) && !isAlreadyImported(experience)"
                      class="mt-2 text-primary text-xs hover:underline"
                      @click.stop="openSplit(index)"
                    >
                      {{ $t('experience_split.split_action') }}
                    </button>
                    <p v-if="isAlreadyImported(experience)" class="text-yellow-500 text-xs mt-1 flex items-center gap-1">
                      <icon name="solar:info-circle-outline" class="h-3 w-3" />
                      {{ $t('linkedin_import.already_imported') }}
                    </p>
                    <p
                      v-if="experience.confidence !== undefined && experience.confidence < 0.70 && !isAlreadyImported(experience)"
                      class="text-orange-400 text-xs mt-1 flex items-center gap-1"
                      :title="$t('linkedin_import.confidence_tooltip', { score: Math.round(experience.confidence * 100) })"
                    >
                      <icon name="solar:shield-warning-outline" class="h-3 w-3" />
                      {{ $t('linkedin_import.low_confidence_warning') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="!scrapeError" class="text-center py-8">
            <icon name="solar:box-minimalistic-outline" class="h-12 w-12 text-gray-500 mx-auto mb-3" />
            <p class="text-gray-400">{{ $t('linkedin_import.no_experiences_found') }}</p>
          </div>
        </div>

        <!-- Step 4: Importing -->
        <div v-if="currentStep === 'importing'" class="flex flex-col items-center justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p class="text-white text-lg mb-2">{{ $t('linkedin_import.importing_title') }}</p>
          <p class="text-gray-400 text-sm">{{ $t('linkedin_import.importing_description', { count: selectedCount }) }}</p>
        </div>

        <!-- Step 5: Success -->
        <div v-if="currentStep === 'success'" class="flex flex-col items-center justify-center py-12">
          <icon name="solar:check-circle-bold" class="h-16 w-16 text-green-400 mb-4" />
          <p class="text-white text-lg mb-2">{{ $t('linkedin_import.success_title') }}</p>
          <p class="text-gray-400 text-sm text-center">
            {{ $t('linkedin_import.success_description', { created: importResult?.created_count || 0 }) }}
          </p>
          <p v-if="importResult?.failed_count > 0" class="text-yellow-400 text-sm mt-2">
            {{ $t('linkedin_import.some_failed', { failed: importResult.failed_count }) }}
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="px-10 pb-10 pt-2 flex gap-4">
        <b-button v-if="currentStep !== 'success'" light block @click="handleCancel">
          {{ $t('common.cancel') }}
        </b-button>

        <!-- Input step: PDF mode -->
        <b-button
          v-if="currentStep === 'input' && importMode === 'pdf'"
          block
          :disabled="!selectedFile"
          @click="handleParsePdf"
        >
          {{ $t('linkedin_import.import_button') }}
        </b-button>

        <!-- Input step: URL mode (normal) -->
        <b-button
          v-if="currentStep === 'input' && importMode === 'url' && !scrapeHasFailed && scraperAvailable"
          block
          :disabled="!linkedinUrl || isScraping || isCheckingStatus"
          :loading="isScraping"
          @click="handleScrape"
        >
          {{ $t('linkedin_import.import_button') }}
        </b-button>

        <!-- Input step: URL mode (fallback to PDF after scrape failure) -->
        <b-button
          v-if="currentStep === 'input' && importMode === 'url' && scrapeHasFailed"
          block
          :disabled="!selectedFile"
          @click="handleParsePdf"
        >
          {{ $t('linkedin_import.import_button') }}
        </b-button>

        <!-- Select step -->
        <b-button
          v-if="currentStep === 'select'"
          block
          :disabled="selectedCount === 0"
          @click="handleImport"
        >
          {{ $t('linkedin_import.confirm_import', { count: selectedCount }) }}
        </b-button>

        <!-- Success step -->
        <b-button
          v-if="currentStep === 'success'"
          block
          @click="handleClose"
        >
          {{ $t('common.close') }}
        </b-button>
      </div>
    </template>
  </b-modal>

  <b-split-experience-modal
    v-model="splitModalOpen"
    :source="splitSource"
    @confirm="onSplitConfirm"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import BModal from '~/components/modal/BModal.vue';
import BTextField from '~/components/forms/BTextField.vue';
import BButton from '~/components/forms/BButton.vue';
import type { ScrapeLinkedInResponse, LinkedInExperience, ScraperStatusResponse } from '~/api/v2/linkedin/LinkedInRepository';
import type { BulkExperienceItem, BulkCreateExperiencesResponse } from '~/api/v2/experiences/ExperienceRepository';
import BSplitExperienceModal from '~/components/modal/experience/BSplitExperienceModal.vue';
import { isSplittable, type SplitPeriod } from '~/help/experienceSplit';

interface Props {
  companyId: string;
  existingExperiences?: Array<{
    id: string;
    title?: string;
    start_at?: string;
    end_at?: string | null;
    company?: { public_name?: string; business_name?: string };
  }>;
}

const props = withDefaults(defineProps<Props>(), {
  existingExperiences: () => []
});

const emit = defineEmits(['imported']);

const { $api } = useNuxtApp();
const { t } = useI18n();

const model = defineModel({ default: false });

type Step = 'input' | 'scraping' | 'select' | 'importing' | 'success';
type ImportMode = 'pdf' | 'url';

const currentStep = ref<Step>('input');
const importMode = ref<ImportMode>('pdf');
const linkedinUrl = ref('');
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const fileInputRef = ref<HTMLInputElement | null>(null);
const isScraping = ref(false);
const scrapeError = ref<string | null>(null);
const scrapeHasFailed = ref(false);
const scrapedExperiences = ref<LinkedInExperience[]>([]);
const selectedExperiences = ref<boolean[]>([]);
const importResult = ref<BulkCreateExperiencesResponse | null>(null);

// Split-a-long-CDI sub-modal state (ticket #136)
const splitModalOpen = ref(false);
const splitSourceIndex = ref<number | null>(null);
const splitSource = ref<{ title: string; company?: string; start: string; end: string | null }>({ title: '', start: '', end: null });

// Scraper availability status
const isCheckingStatus = ref(false);
const scraperAvailable = ref(true);
const scraperUnavailableReason = ref<string | null>(null);

const selectedCount = computed(() => selectedExperiences.value.filter(Boolean).length);
const allSelected = computed(() => {
  const selectableCount = scrapedExperiences.value.filter(exp => !isAlreadyImported(exp)).length;
  return selectableCount > 0 && selectedCount.value === selectableCount;
});

/**
 * Contract type mappings from LinkedIn indicators to BIG contract types.
 * LinkedIn often shows company names as "CompanyName · ContractType"
 */
const CONTRACT_TYPE_MAPPINGS: Record<string, string> = {
  // French - Freelance
  'indépendant': 'freelance',
  'independant': 'freelance',
  'freelance': 'freelance',
  'auto-entrepreneur': 'freelance',
  'autoentrepreneur': 'freelance',
  'travailleur indépendant': 'freelance',
  // English - Freelance
  'self-employed': 'freelance',
  'self employed': 'freelance',
  'contractor': 'freelance',
  'independent contractor': 'freelance',
  // French - Permanent (CDI)
  'cdi': 'permanent',
  'temps plein': 'permanent',
  'plein temps': 'permanent',
  'permanent': 'permanent',
  // English - Permanent
  'full-time': 'permanent',
  'full time': 'permanent',
  // French - Fixed term (CDD)
  'cdd': 'fixed_term',
  'temps partiel': 'fixed_term',
  'temporaire': 'fixed_term',
  'intérim': 'fixed_term',
  'interim': 'fixed_term',
  // English - Fixed term
  'part-time': 'fixed_term',
  'part time': 'fixed_term',
  'contract': 'fixed_term',
  'temporary': 'fixed_term',
  // Internship
  'stage': 'internship',
  'stagiaire': 'internship',
  'internship': 'internship',
  'intern': 'internship',
  // Work-study
  'alternance': 'work_study',
  'alternant': 'work_study',
  'apprentissage': 'work_study',
  'apprenti': 'work_study',
  'work-study': 'work_study',
  'apprenticeship': 'work_study',
};

interface ParsedCompanyInfo {
  companyName: string;
  contractType?: string;
}

/**
 * Parse LinkedIn company info to extract clean company name and contract type.
 * LinkedIn often formats company info as "CompanyName · ContractType"
 *
 * @example
 * parseLinkedInCompanyInfo("PitchFlow · Indépendant")
 * // Returns: { companyName: "PitchFlow", contractType: "freelance" }
 *
 * parseLinkedInCompanyInfo("Google · Full-time")
 * // Returns: { companyName: "Google", contractType: "permanent" }
 *
 * parseLinkedInCompanyInfo("Acme Corp")
 * // Returns: { companyName: "Acme Corp", contractType: undefined }
 */
const parseLinkedInCompanyInfo = (rawCompany: string): ParsedCompanyInfo => {
  if (!rawCompany) {
    return { companyName: rawCompany };
  }

  // LinkedIn uses " · " (middle dot with spaces) as separator
  // Note: We only use " · " because other separators like " | " or " - "
  // can be part of company names (e.g., "Additi Média | Groupe Ouest-France · Freelance")
  const separators = [' · '];

  for (const separator of separators) {
    if (rawCompany.includes(separator)) {
      const parts = rawCompany.split(separator);

      if (parts.length >= 2) {
        const companyName = parts[0].trim();
        const potentialContractType = parts.slice(1).join(separator).trim().toLowerCase();

        // Check if the second part is a known contract type
        const contractType = CONTRACT_TYPE_MAPPINGS[potentialContractType];

        if (contractType) {
          return { companyName, contractType };
        }

        // Check if it contains a known contract type keyword
        for (const [keyword, type] of Object.entries(CONTRACT_TYPE_MAPPINGS)) {
          if (potentialContractType.includes(keyword)) {
            return { companyName, contractType: type };
          }
        }
      }
    }
  }

  // No separator found or no contract type detected, return as-is
  return { companyName: rawCompany };
};

/**
 * A scraped experience is splittable if it's a long CDI. contract_type is
 * derived from the company suffix, like in handleImport. Nothing is created
 * yet at this point, so `requireDeclarative` is intentionally not passed.
 */
const scrapedIsSplittable = (exp: LinkedInExperience): boolean => {
  const { contractType } = parseLinkedInCompanyInfo(exp.company || '');
  return isSplittable({
    contract_type: exp.contract_type || contractType,
    start: exp.start_date,
    end: exp.is_current ? null : exp.end_date,
  });
};

const openSplit = (index: number) => {
  const exp = scrapedExperiences.value[index];
  const { companyName } = parseLinkedInCompanyInfo(exp.company || '');
  splitSourceIndex.value = index;
  splitSource.value = {
    title: exp.title || '',
    company: companyName,
    start: exp.start_date || '',
    end: exp.is_current ? null : (exp.end_date || null),
  };
  splitModalOpen.value = true;
};

// Replace the source scraped experience with N sub-period entries carrying skill_ids.
const onSplitConfirm = (segments: SplitPeriod[]) => {
  if (splitSourceIndex.value === null) return;
  const source = scrapedExperiences.value[splitSourceIndex.value];
  const { contractType } = parseLinkedInCompanyInfo(source.company || '');
  const newEntries: LinkedInExperience[] = segments.map((seg) => ({
    ...source,
    description: undefined,
    title: seg.title,
    start_date: seg.start,
    end_date: seg.end ?? undefined,
    is_current: seg.end === null,
    skill_ids: seg.skill_ids,
    contract_type: source.contract_type || contractType,
  }));
  scrapedExperiences.value.splice(splitSourceIndex.value, 1, ...newEntries);
  // Re-sync the selection array: keep everything selected.
  selectedExperiences.value = scrapedExperiences.value.map(() => true);
  splitSourceIndex.value = null;
};

/**
 * Check if a LinkedIn experience is already imported.
 *
 * Matching logic:
 * 1. Primary match: Company + Start Date (handles title changes on LinkedIn)
 * 2. Fallback match: Company + Title (when dates are not available)
 */
const isAlreadyImported = (experience: LinkedInExperience): boolean => {
  if (!props.existingExperiences.length) return false;

  // Parse company info to get clean company name (without contract type suffix)
  const { companyName } = parseLinkedInCompanyInfo(experience.company || '');
  const normalizedCompany = companyName.toLowerCase().trim();
  const normalizedTitle = (experience.title || '').toLowerCase().trim();
  const linkedInStartDate = experience.start_date || '';

  // Skip if we don't have company info
  if (!normalizedCompany) return false;

  return props.existingExperiences.some(existing => {
    const existingCompany = (existing.company?.public_name || existing.company?.business_name || '').toLowerCase().trim();
    const existingTitle = (existing.title || '').toLowerCase().trim();
    const existingStartDate = existing.start_at || '';

    // Skip if existing experience doesn't have company info
    if (!existingCompany) return false;

    // Check company match (fuzzy match)
    const companyMatches = existingCompany.includes(normalizedCompany) ||
                          normalizedCompany.includes(existingCompany);

    if (!companyMatches) return false;

    // Primary match: Company + Start Date
    // LinkedIn format: "Jan 2020" or "2020", existing format: "2020-01-15" (ISO date)
    if (linkedInStartDate && existingStartDate) {
      const linkedInDateNormalized = normalizeLinkedInDate(linkedInStartDate);
      const existingDateNormalized = normalizeExistingDate(existingStartDate);

      if (linkedInDateNormalized && existingDateNormalized) {
        // Compare year and month
        if (linkedInDateNormalized === existingDateNormalized) {
          return true;
        }
      }
    }

    // Fallback match: Company + Title (when dates not available)
    if (normalizedTitle && existingTitle) {
      const titleMatches = existingTitle === normalizedTitle;
      if (titleMatches) {
        return true;
      }
    }

    return false;
  });
};

/**
 * Normalize LinkedIn date format to YYYY-MM
 * LinkedIn format examples: "Jan 2020", "January 2020", "2020"
 */
const normalizeLinkedInDate = (dateStr: string): string | null => {
  if (!dateStr) return null;

  const monthMap: Record<string, string> = {
    'jan': '01', 'january': '01',
    'feb': '02', 'february': '02',
    'mar': '03', 'march': '03',
    'apr': '04', 'april': '04',
    'may': '05',
    'jun': '06', 'june': '06',
    'jul': '07', 'july': '07',
    'aug': '08', 'august': '08',
    'sep': '09', 'september': '09',
    'oct': '10', 'october': '10',
    'nov': '11', 'november': '11',
    'dec': '12', 'december': '12',
  };

  const normalized = dateStr.toLowerCase().trim();

  // Try to extract year and month
  const parts = normalized.split(/\s+/);

  if (parts.length === 1) {
    // Just a year like "2020"
    const year = parts[0];
    if (/^\d{4}$/.test(year)) {
      return `${year}-01`; // Default to January if only year is provided
    }
  } else if (parts.length >= 2) {
    // Month and year like "Jan 2020" or "January 2020"
    const monthStr = parts[0];
    const yearStr = parts[parts.length - 1];

    const month = monthMap[monthStr];
    if (month && /^\d{4}$/.test(yearStr)) {
      return `${yearStr}-${month}`;
    }
  }

  return null;
};

/**
 * Normalize existing experience date (ISO format) to YYYY-MM
 * Existing format: "2020-01-15" or "2020-01-15T00:00:00Z"
 */
const normalizeExistingDate = (dateStr: string): string | null => {
  if (!dateStr) return null;

  // Extract YYYY-MM from ISO date
  const match = dateStr.match(/^(\d{4})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }

  return null;
};

const toggleExperience = (index: number) => {
  selectedExperiences.value[index] = !selectedExperiences.value[index];
};

const toggleSelectAll = () => {
  const newValue = !allSelected.value;
  selectedExperiences.value = scrapedExperiences.value.map((exp) =>
    isAlreadyImported(exp) ? false : newValue
  );
};

const handleScrape = async () => {
  if (!linkedinUrl.value) return;

  currentStep.value = 'scraping';
  scrapeError.value = null;
  isScraping.value = true;

  try {
    const response: ScrapeLinkedInResponse = await $api().v2.linkedin.scrapeProfile({ url: linkedinUrl.value });

    if (response.success && response.experiences) {
      scrapedExperiences.value = response.experiences;
      selectedExperiences.value = response.experiences.map(exp =>
        !isAlreadyImported(exp) && (exp.confidence === undefined || exp.confidence >= 0.70)
      );
      currentStep.value = 'select';
    } else {
      // Scrape failed → stay on URL tab, show PDF fallback
      scrapeHasFailed.value = true;
      currentStep.value = 'input';
    }
  } catch (error: any) {
    // Scrape failed → stay on URL tab, show PDF fallback
    scrapeHasFailed.value = true;
    currentStep.value = 'input';
  } finally {
    isScraping.value = false;
  }
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    selectedFile.value = input.files[0];
  }
};

const handleFileDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files[0] && files[0].type === 'application/pdf') {
    selectedFile.value = files[0];
  }
};

const handleParsePdf = async () => {
  if (!selectedFile.value) return;

  currentStep.value = 'scraping';
  scrapeError.value = null;
  isScraping.value = true;

  try {
    const response: ScrapeLinkedInResponse = await $api().v2.linkedin.parsePdf(selectedFile.value);

    if (response.success && response.experiences) {
      scrapedExperiences.value = response.experiences;
      selectedExperiences.value = response.experiences.map(exp =>
        !isAlreadyImported(exp) && (exp.confidence === undefined || exp.confidence >= 0.70)
      );
      currentStep.value = 'select';
    } else {
      console.warn('[LinkedIn Import] PDF parse failed:', response.error);
      scrapeError.value = response.error || t('linkedin_import.pdf_parse_error');
      currentStep.value = 'select';
    }
  } catch (error: any) {
    console.warn('[LinkedIn Import] PDF parse error:', error?.data?.data?.detail || error?.message);
    scrapeError.value = t('linkedin_import.pdf_parse_error');
    currentStep.value = 'select';
  } finally {
    isScraping.value = false;
  }
};

const handleImport = async () => {
  if (selectedCount.value === 0) return;

  currentStep.value = 'importing';

  try {
    const selectedList = scrapedExperiences.value.filter((_, index) => selectedExperiences.value[index]);

    const experiences: BulkExperienceItem[] = selectedList.map(exp => {
      // Parse company info to extract clean name and detect contract type
      const { companyName, contractType } = parseLinkedInCompanyInfo(exp.company || '');

      return {
        title: exp.title || 'Poste non specifie',
        company_name: companyName || 'Entreprise non specifiee',
        description: exp.description || undefined,
        start_date: exp.start_date || undefined,
        end_date: exp.end_date || undefined,
        is_current: exp.is_current || false,
        location: exp.location || undefined,
        contract_type: exp.contract_type || contractType,
        skill_ids: exp.skill_ids || undefined,
      };
    });

    const result = await $api().v2.experiences.bulkCreate(props.companyId, { experiences });
    importResult.value = result;
    currentStep.value = 'success';
  } catch (error: any) {
    scrapeError.value = error?.data?.data?.detail || error?.message || t('linkedin_import.import_error');
    currentStep.value = 'select';
  }
};

const handleCancel = () => {
  model.value = false;
};

const handleClose = () => {
  emit('imported');
  model.value = false;
};

/**
 * Check if the LinkedIn scraper service is available.
 */
const checkScraperStatus = async () => {
  isCheckingStatus.value = true;
  scraperAvailable.value = true;
  scraperUnavailableReason.value = null;

  try {
    const status: ScraperStatusResponse = await $api().v2.linkedin.getStatus();
    scraperAvailable.value = status.available;

    if (!status.available) {
      // Build user-friendly message
      if (status.reason === 'All accounts in cooldown' && status.next_available_in_seconds) {
        const minutes = Math.ceil(status.next_available_in_seconds / 60);
        scraperUnavailableReason.value = t('linkedin_import.unavailable_cooldown', { minutes });
      } else if (status.reason === 'No accounts logged in') {
        scraperUnavailableReason.value = t('linkedin_import.unavailable_not_logged_in');
      } else {
        scraperUnavailableReason.value = t('linkedin_import.unavailable_generic');
      }
    }
  } catch (error) {
    // If status check fails, assume service is unavailable
    scraperAvailable.value = false;
    scraperUnavailableReason.value = t('linkedin_import.unavailable_generic');
  } finally {
    isCheckingStatus.value = false;
  }
};

// Reset state when modal opens
watch(model, async (isOpen) => {
  if (isOpen) {
    currentStep.value = 'input';
    linkedinUrl.value = '';
    scrapeError.value = null;
    scrapedExperiences.value = [];
    selectedExperiences.value = [];
    importResult.value = null;
    selectedFile.value = null;
    isDragging.value = false;
    scrapeHasFailed.value = false;

    // Only check scraper status if URL mode is selected
    if (importMode.value === 'url') {
      await checkScraperStatus();
    }
  }
});

// Check scraper status when switching to URL mode
watch(importMode, async (mode) => {
  if (mode === 'url' && currentStep.value === 'input') {
    await checkScraperStatus();
  }
});
</script>
